import { Spin, Typography } from 'antd';
import { SwapOutlined, LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import type { ItemType } from 'antd/es/menu/interface';
import { APP_STATUS } from '@aid/shared';
import { useAppManifest } from './useAppManifest';
import { navigateToApp } from './manifest-client';

const { Text } = Typography;

export function useAppSwitcherItems(currentAppId = 'aid'): {
  items: ItemType[];
  loading: boolean;
} {
  const { manifest, loading, error } = useAppManifest();

  if (loading) {
    return {
      items: [
        {
          key: 'switch-loading',
          icon: <LoadingOutlined />,
          label: <Spin size="small" />,
          disabled: true,
        },
      ],
      loading: true,
    };
  }

  if (error || !manifest) {
    return {
      items: [
        {
          key: 'switch-error',
          icon: <WarningOutlined />,
          label: 'Apps unavailable',
          disabled: true,
        },
      ],
      loading: false,
    };
  }

  const items: ItemType[] = [];

  const activeApps = manifest.apps.filter((app) => app.status === APP_STATUS.ACTIVE);
  const disabledApps = manifest.apps.filter((app) => app.status === APP_STATUS.DISABLED);

  for (const app of activeApps) {
    const isCurrent = app.id === currentAppId;
    items.push({
      key: `switch-${app.id}`,
      label: isCurrent ? `${app.label} (current)` : app.label,
      disabled: isCurrent,
      onClick: isCurrent ? undefined : () => navigateToApp(app.url),
    });
  }

  if (disabledApps.length > 0) {
    if (items.length > 0) {
      items.push({ type: 'divider' });
    }

    for (const app of disabledApps) {
      items.push({
        key: `switch-disabled-${app.id}`,
        label: <Text type="secondary">{app.label}</Text>,
        disabled: true,
      });
    }
  }

  return { items, loading: false };
}

export function useSwitchAppMenuItem(currentAppId = 'aid'): ItemType {
  const { items, loading } = useAppSwitcherItems(currentAppId);

  return {
    key: 'switch',
    icon: <SwapOutlined />,
    label: loading ? 'Loading...' : 'Switch App',
    children: items,
  };
}
